import {
  getTokensByType,
  inspectFilterBranchesInSearchQuery,
  replaceAllFiltersByTypeInSearchQuery,
  replaceFilterBranchInSearchQuery,
  replaceTokensDifferentially,
  tokenizeQuery,
  type TokenType,
} from '@github-ui/filter-query'

export function getRepoPullsFilterState(
  query: string,
  matchingTypes: readonly TokenType[],
  structuralUpdatesEnabled: boolean,
): string | undefined {
  if (!structuralUpdatesEnabled) {
    if (query.length > 1000) return undefined
    return tokenizeQuery(query)
      .filter(segment => segment.type === 'filter' && matchingTypes.includes(segment.key.toLowerCase() as TokenType))
      .flatMap(segment =>
        segment.type === 'filter'
          ? segment.values.map(value => `${segment.isNegated ? '-' : ''}${segment.key.toLowerCase()}:${value}`)
          : [],
      )
      .toSorted()
      .join(' ')
  }

  const inspection = inspectFilterBranchesInSearchQuery(query, matchingTypes)
  if (!inspection.isSafe) return undefined

  return inspection.branches
    .map(branch => JSON.stringify([branch.isNegated, branch.key, branch.values]))
    .toSorted()
    .join('\n')
}

export function getRepoPullsFilterPickerTokens(query: string, type: TokenType, structuralUpdatesEnabled: boolean) {
  if (!structuralUpdatesEnabled) return getTokensByType(query, type)

  const inspection = inspectFilterBranchesInSearchQuery(query, [type])
  if (!inspection.isSafe) return []
  if (
    inspection.branches.some(branch => branch.isNegated || branch.key !== type) ||
    (inspection.hasBooleanStructure && inspection.branches.length > 1)
  ) {
    return []
  }
  return inspection.branches.flatMap(branch => branch.values)
}

export function getRepoPullsPositiveFilterPickerTokens(
  query: string,
  type: TokenType,
  structuralUpdatesEnabled: boolean,
) {
  if (!structuralUpdatesEnabled) return getTokensByType(query, type)

  const inspection = inspectFilterBranchesInSearchQuery(query, [type])
  if (!inspection.isSafe) return []

  const tokens = inspection.branches
    .filter(branch => !branch.isNegated && branch.key === type)
    .flatMap(branch => branch.values)
  const identities = new Set(tokens.map(token => token.toLowerCase()))
  return identities.size === tokens.length ? tokens : []
}

export function replaceRepoPullsFilterInQuery(
  query: string,
  tokens: readonly string[],
  type: TokenType,
  structuralUpdatesEnabled: boolean,
) {
  if (!structuralUpdatesEnabled) {
    return type === 'author' || type === 'assignee' || type === 'milestone'
      ? replaceAllFiltersByTypeInSearchQuery(query, [...tokens], type)
      : replaceTokensDifferentially(query, [...tokens], type)
  }

  return replaceFilterBranchInSearchQuery(query, tokens, type, {
    treatNoValueAsLiteral: type === 'label' || type === 'milestone',
    replaceMultipleFlatBranches: true,
    reconcilePositiveBranchSubset: type === 'label',
  })
}
